//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  CampaignSubTypeFilter,
  CampaignTypeFilter,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import { getIsCreatorCampaignsEnabled } from '@campaigns/components/campaigns-overview/selectors/getIsCreatorCampaignsEnabled';
import { useIsRetailerClient } from '@campaigns/hooks/useIsRetailerClient';
import HR from '@components/atoms/hr/HR';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { CheckboxSetFilter } from '@components/media-filters/CheckboxSetFilter';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import {
  FilterOption,
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { MediaLibraryTrackingIds } from '@media-library/constants/mediaLibraryTrackingIds';

export const getDefaultCampaignTypeFilterSection = (
  isCreatorCampaignsEnabled: boolean,
): FilterSection<string> => {
  return {
    options: [
      {
        isSelected: false,
        label: 'Not from Campaigns',
        value: CampaignTypeFilter.NONE,
        trackingId: MediaLibraryTrackingIds.CAMPAIGNS_NONE_CHECKBOX,
      },
      ...(isCreatorCampaignsEnabled
        ? [
            {
              isSelected: false,
              label: 'Creator Campaigns',
              value: CampaignTypeFilter.CREATOR,
              trackingId: MediaLibraryTrackingIds.CAMPAIGNS_CREATOR_CHECKBOX,
            },
          ]
        : []),
      {
        isSelected: false,
        label: 'Sampling Campaigns',
        value: CampaignTypeFilter.SAMPLING,
        trackingId: MediaLibraryTrackingIds.CAMPAIGNS_SAMPLING_CHECKBOX,
      },
    ],
    paramKey: 'campaignTypes',
  };
};

export const getDefaultCampaignSubTypeFilterSection = (
  isRetailerClient: boolean | undefined,
): FilterSection<string> => {
  return isRetailerClient
    ? {
        options: [
          {
            isSelected: false,
            label: 'My Campaigns',
            value: CampaignSubTypeFilter.SAMPLING_RETAILER,
            trackingId: MediaLibraryTrackingIds.CAMPAIGNS_RETAILER_CHECKBOX,
          },
          {
            isSelected: false,
            label: 'Brand Campaigns',
            value: CampaignSubTypeFilter.SAMPLING_BRAND,
            trackingId: MediaLibraryTrackingIds.CAMPAIGNS_BRAND_CHECKBOX,
          },
        ],
        paramKey: 'campaignSubTypes',
      }
    : {
        options: [],
        paramKey: 'campaignSubTypes',
      };
};

const isCampaignFilterSelected = (
  section: FilterSection<FilterValue> | undefined,
  campaignFilter: CampaignTypeFilter | CampaignSubTypeFilter,
): boolean => {
  return (
    section?.options.some(
      (option) => option.isSelected && option.value === campaignFilter,
    ) ?? false
  );
};

export interface CampaignsFilterProps {
  isDisabled?: boolean;
  isInitiallyCollapsed?: boolean;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  section: FilterSection<string> | undefined;
  subsection: FilterSection<string> | undefined;
}

export const CampaignsFilter = ({
  isDisabled,
  isInitiallyCollapsed,
  onChange,
  section,
  subsection,
}: CampaignsFilterProps) => {
  const isCreatorCampaignsEnabled = useSelector(getIsCreatorCampaignsEnabled);
  const { data: isRetailer, isPending } = useIsRetailerClient();

  const campaignTypeSection: FilterSection<string> = {
    paramKey: 'campaignTypes',
    options: (
      section ?? getDefaultCampaignTypeFilterSection(isCreatorCampaignsEnabled)
    ).options,
  };

  const campaignSubTypeSection: FilterSection<string> = {
    paramKey: 'campaignSubTypes',
    options: (subsection ?? getDefaultCampaignSubTypeFilterSection(isRetailer))
      .options,
  };

  if (isPending) {
    return <CenteredSpinner />;
  }

  return (
    <>
      <StyledCollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        title={FilterSectionTitle[campaignTypeSection.paramKey]}
      >
        <CheckboxSetFilter
          isDisabled={isDisabled}
          onChange={(sections) => {
            const isNoneCampaignTypeSelected = isCampaignFilterSelected(
              sections.get(campaignTypeSection.paramKey),
              CampaignTypeFilter.NONE,
            );
            const wasNoneCampaignTypeSelected = isCampaignFilterSelected(
              section,
              CampaignTypeFilter.NONE,
            );

            const updatedSections: [
              MediaFilterParamKey,
              FilterSection<FilterValue>,
            ][] = (() => {
              // Toggle off all campaign filters if "None" is selected
              if (!wasNoneCampaignTypeSelected && isNoneCampaignTypeSelected) {
                return [
                  [
                    campaignTypeSection.paramKey,
                    {
                      ...campaignTypeSection,
                      options: campaignTypeSection.options.map((option) => ({
                        ...option,
                        isSelected: option.value === CampaignTypeFilter.NONE,
                      })),
                    },
                  ],
                  [
                    campaignSubTypeSection.paramKey,
                    {
                      ...campaignSubTypeSection,
                      options: campaignSubTypeSection.options.map((option) => ({
                        ...option,
                        isSelected: false,
                      })),
                    },
                  ],
                ];
              }

              // Deselect Sampling subtypes if Sampling is deselected
              const isSamplingSelected = isCampaignFilterSelected(
                sections.get(campaignTypeSection.paramKey),
                CampaignTypeFilter.SAMPLING,
              );
              const wasSamplingSelected = isCampaignFilterSelected(
                section,
                CampaignTypeFilter.SAMPLING,
              );

              const subOptions: FilterOption<string>[] =
                campaignSubTypeSection.options.map((option) => ({
                  ...option,
                  isSelected: (() => {
                    if (wasSamplingSelected && !isSamplingSelected) {
                      return false;
                    }
                    return option.isSelected;
                  })(),
                }));

              // Deselect "None" if any other campaign type is selected
              const isAnyCampaignTypeSelected =
                sections
                  .get(campaignTypeSection.paramKey)
                  ?.options.some(
                    (option) =>
                      option.value !== CampaignTypeFilter.NONE &&
                      option.isSelected,
                  ) ?? false;

              const parentOptions =
                sections
                  .get(campaignTypeSection.paramKey)
                  ?.options.map((option) => ({
                    ...option,
                    isSelected: (() => {
                      if (
                        option.value === CampaignTypeFilter.NONE &&
                        option.isSelected &&
                        isAnyCampaignTypeSelected
                      ) {
                        return false;
                      }
                      return option.isSelected;
                    })(),
                  })) ?? [];

              return [
                [
                  campaignTypeSection.paramKey,
                  {
                    paramKey: campaignTypeSection.paramKey,
                    options: parentOptions,
                  },
                ],
                [
                  campaignSubTypeSection.paramKey,
                  {
                    ...campaignSubTypeSection,
                    options: subOptions,
                  },
                ],
              ];
            })();
            onChange(
              new Map<MediaFilterParamKey, FilterSection<FilterValue>>(
                updatedSections,
              ),
            );
          }}
          section={campaignTypeSection}
        />
        {isRetailer && (
          <SubsectionContainer>
            <CheckboxSetFilter
              isDisabled={isDisabled}
              onChange={(sections) => {
                const isMyCampaignsSelected = isCampaignFilterSelected(
                  sections.get(campaignSubTypeSection.paramKey),
                  CampaignSubTypeFilter.SAMPLING_RETAILER,
                );
                const isBrandCampaignsSelected = isCampaignFilterSelected(
                  sections.get(campaignSubTypeSection.paramKey),
                  CampaignSubTypeFilter.SAMPLING_BRAND,
                );

                const parentOptions: FilterOption<FilterValue>[] =
                  campaignTypeSection.options.map((option) => ({
                    ...option,
                    isSelected: (() => {
                      if (option.value === CampaignTypeFilter.SAMPLING) {
                        return (
                          isMyCampaignsSelected ||
                          isBrandCampaignsSelected ||
                          option.isSelected
                        );
                      } else if (
                        option.value === CampaignTypeFilter.NONE &&
                        (isMyCampaignsSelected || isBrandCampaignsSelected)
                      ) {
                        return false;
                      } else {
                        return option.isSelected;
                      }
                    })(),
                  }));

                const updatedSections: [
                  MediaFilterParamKey,
                  FilterSection<FilterValue>,
                ][] = [
                  [
                    campaignSubTypeSection.paramKey,
                    {
                      paramKey: campaignSubTypeSection.paramKey,
                      options:
                        sections.get(campaignSubTypeSection.paramKey)
                          ?.options ?? [],
                    },
                  ],
                  [
                    campaignTypeSection.paramKey,
                    {
                      ...campaignTypeSection,
                      options: parentOptions,
                    },
                  ],
                ];

                onChange(
                  new Map<MediaFilterParamKey, FilterSection<FilterValue>>(
                    updatedSections,
                  ),
                );
              }}
              section={campaignSubTypeSection}
            />
          </SubsectionContainer>
        )}
      </StyledCollapsibleSection>
      <HR />
    </>
  );
};

const SubsectionContainer = styled.div`
  padding-left: ${({ theme }) => theme.spacings[4]};
`;

const StyledCollapsibleSection = styled(CollapsibleSection)`
  & div {
    gap: ${({ theme }) => theme.spacings[1]};
  }
`;
